import * as React from 'react'
import { AlertCircle as WarningCircleIcon, Workflow as FlowArrowIcon } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { decodeRouteParam } from '@atbash-labs/plantview-links'
import {
  useGetPlantSiteDetailQuery,
  usePlantSessionPaint,
  collectAdditionalProperties,
  type PlantLineSummary
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { plantEquipmentIndexHref } from '../../lib/plant-links'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { PlantStrayPill } from './entity/PlantStrayPill'
import { PlantHref } from './PlantHref'
import {
  SessionBreadcrumbCurrent,
  SessionGhostTile,
  SessionMarkAffordances,
  SessionRollupPill,
  SessionTitleMark,
  countSessionMarks,
  sessionDetailHeaderClass,
  sessionMarkClass,
  useVisibleGhosts
} from './session-mark-ui'
import { SiteFeedsFlow } from './SiteFeedsFlow'
import { UnorderedNotice } from './UnorderedNotice'

/**
 * `/plant/equipment/sites/:siteId` — single-site detail.
 *
 * Renders site KPIs (lines + equipment counts), the optional FEEDS
 * connectivity diagram between this site's lines, and a grid of line
 * tiles. Each tile shows the line's instantiated process (cross-tab
 * link to the Processes view via `plantHref`) and its subline /
 * equipment counts.
 */
const SiteDetailPage = (): React.ReactElement => {
  const { siteId = '' } = useParams<{ siteId: string }>()
  const decodedSiteId = decodeRouteParam(siteId)

  const { data, isLoading, isError } = useGetPlantSiteDetailQuery(decodedSiteId, {
    skip: !decodedSiteId
  })

  const site = data?.site
  const lines = React.useMemo(() => data?.lines ?? [], [data?.lines])
  const feeds = React.useMemo(() => data?.feeds ?? [], [data?.feeds])
  const totalEquipment = React.useMemo(
    () => lines.reduce((acc, line) => acc + line.equipmentCount, 0),
    [lines]
  )
  const hasFeeds = feeds.length > 0 && lines.length > 1
  const noFeedsBetweenLines = feeds.length === 0 && lines.length > 1
  const additionalProperties = React.useMemo(
    () =>
      site ? collectAdditionalProperties('Site', site as unknown as Record<string, unknown>) : [],
    [site]
  )
  const {
    active,
    markFor,
    equipmentRollupByContainerId,
    equipmentTouchedRollupByContainerId,
    equipmentDeletedRollupByContainerId,
    deletedRollupByContainerId
  } = usePlantSessionPaint()
  const siteMark = markFor(decodedSiteId)
  const lineGhosts = useVisibleGhosts(decodedSiteId, 'Line')
  const linesAdded = React.useMemo(
    () => lines.filter((line) => markFor(line.id) === 'added').length,
    [lines, markFor]
  )
  const linesModified = React.useMemo(
    () => lines.filter((line) => markFor(line.id) === 'touched').length,
    [lines, markFor]
  )
  const equipmentAdded = React.useMemo(() => {
    if (!active) return 0
    const hasLiveIds = lines.some((line) => line.equipmentIds !== undefined)
    if (hasLiveIds) {
      return lines.reduce(
        (sum, line) => sum + countSessionMarks(line.equipmentIds ?? [], markFor, 'added'),
        0
      )
    }
    return equipmentRollupByContainerId.get(decodedSiteId) ?? 0
  }, [active, decodedSiteId, equipmentRollupByContainerId, lines, markFor])
  const equipmentModified = React.useMemo(() => {
    if (!active) return 0
    const hasLiveIds = lines.some((line) => line.equipmentIds !== undefined)
    if (hasLiveIds) {
      return lines.reduce(
        (sum, line) => sum + countSessionMarks(line.equipmentIds ?? [], markFor, 'touched'),
        0
      )
    }
    return equipmentTouchedRollupByContainerId.get(decodedSiteId) ?? 0
  }, [active, decodedSiteId, equipmentTouchedRollupByContainerId, lines, markFor])
  // Deletes place on the immediate parent (line). Site←line edges are often
  // absent from the write log, so sum per-line equipment deletes — same numbers
  // the line tiles show — instead of only looking up the site container.
  const equipmentDeleted = React.useMemo(() => {
    if (!active) return 0
    const atSite = equipmentDeletedRollupByContainerId.get(decodedSiteId) ?? 0
    if (atSite > 0) return atSite
    return lines.reduce((sum, line) => {
      const atLine =
        equipmentDeletedRollupByContainerId.get(line.id) ??
        deletedRollupByContainerId.get(line.id) ??
        0
      return sum + atLine
    }, 0)
  }, [
    active,
    decodedSiteId,
    deletedRollupByContainerId,
    equipmentDeletedRollupByContainerId,
    lines
  ])
  const linesDeleted = lineGhosts.filter((ghost) => ghost.view === 'deleted').length

  return (
    <div className="flex flex-1 flex-col gap-card">
      <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
        <Link to={plantEquipmentIndexHref()} className="hover:text-ink hover:underline">
          Sites
        </Link>
        <span className="mx-1.5 text-ink-4">/</span>
        <SessionBreadcrumbCurrent entityId={decodedSiteId}>
          {site?.name ?? decodedSiteId}
        </SessionBreadcrumbCurrent>
      </nav>

      {isError ? (
        <DetailFallback
          title="Couldn't load this site"
          hint="The site may have been deleted or the proxy is unreachable."
        />
      ) : isLoading || !data ? (
        <DetailFallback title="Loading site…" hint="" muted />
      ) : (
        <>
          <header className={sessionDetailHeaderClass('flex flex-col gap-1', siteMark)}>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="m-0 text-2xl font-semibold text-ink">{site?.name ?? decodedSiteId}</h1>
              <SessionTitleMark mark={siteMark} />
              <PlantStrayPill isStray={data.isStray} />
            </div>
            {site?.description && (
              <p className="m-0 max-w-3xl text-sm text-ink-3">{site.description}</p>
            )}
          </header>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat
              label="Lines"
              value={lines.length}
              added={linesAdded}
              modified={linesModified}
              deleted={linesDeleted}
            />
            <Stat
              label="Equipment"
              value={totalEquipment}
              added={equipmentAdded}
              modified={equipmentModified}
              deleted={equipmentDeleted}
            />
            <Stat
              label="Sublines"
              value={lines.reduce((acc, line) => acc + line.sublineCount, 0)}
            />
            <Stat
              label="With process"
              value={lines.filter((line) => line.process !== null).length}
            />
          </div>

          {hasFeeds && (
            <section>
              <SectionHeader label="Connectivity · FEEDS" subtitle={`${feeds.length} edges`} />
              <div className="overflow-x-auto rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
                <SiteFeedsFlow siteId={decodedSiteId} lines={lines} feeds={feeds} />
              </div>
            </section>
          )}

          <section>
            <SectionHeader
              label="Lines"
              subtitle={`${lines.length} ${lines.length === 1 ? 'line' : 'lines'}`}
            />
            {noFeedsBetweenLines && (
              <UnorderedNotice>
                {
                  "No FEEDS relationships exist between this site's lines — listed alphabetically, not in process order."
                }
              </UnorderedNotice>
            )}
            {lines.length === 0 && lineGhosts.length === 0 ? (
              <div className="rounded-xl border bg-card p-4 text-sm text-ink-3 shadow-sm">
                This site has no lines yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(20rem,1fr))]">
                {lines.map((line) => (
                  <LineTile key={line.id} siteId={decodedSiteId} line={line} />
                ))}
                {lineGhosts.map((ghost) => (
                  <SessionGhostTile
                    key={ghost.id}
                    name={ghost.name ?? ghost.id}
                    view={ghost.view}
                    icon={
                      <span className="grid size-9 shrink-0 place-items-center rounded-md bg-st-down-bg text-st-down">
                        <FlowArrowIcon size={16} />
                      </span>
                    }
                  />
                ))}
              </div>
            )}
          </section>

          <EntityDetailsInspector entity={data.site} additionalProperties={additionalProperties} />
        </>
      )}
    </div>
  )
}

function LineTile({
  siteId,
  line
}: {
  siteId: string
  line: PlantLineSummary
}): React.ReactElement {
  const {
    markFor,
    rollupByContainerId,
    touchedRollupByContainerId,
    deletedRollupByContainerId,
    equipmentRollupByContainerId,
    equipmentTouchedRollupByContainerId,
    equipmentDeletedRollupByContainerId
  } = usePlantSessionPaint()
  const mark = markFor(line.id)
  const equipmentIds = line.equipmentIds
  const rollup =
    equipmentIds !== undefined
      ? countSessionMarks(equipmentIds, markFor, 'added')
      : (equipmentRollupByContainerId.get(line.id) ?? rollupByContainerId.get(line.id) ?? 0)
  const touchedRollup =
    equipmentIds !== undefined
      ? countSessionMarks(equipmentIds, markFor, 'touched')
      : (equipmentTouchedRollupByContainerId.get(line.id) ??
        touchedRollupByContainerId.get(line.id) ??
        0)
  const deletedRollup =
    equipmentDeletedRollupByContainerId.get(line.id) ?? deletedRollupByContainerId.get(line.id) ?? 0
  return (
    <div
      className={sessionMarkClass(
        'flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      <PlantHref
        id={line.id}
        label="Line"
        node={{ ...line, siteId }}
        className="flex min-w-0 flex-1 items-center gap-3 no-underline"
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-bg-sunk text-ink-3">
          <FlowArrowIcon size={16} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="block min-w-0 flex-1 truncate text-sm font-semibold text-ink">
              {line.name}
              {line.line_code && (
                <span className="ml-1 font-normal text-ink-3">· {line.line_code}</span>
              )}
            </span>
            <SessionMarkAffordances mark={mark} />
          </span>
          {line.description && (
            <span className="block truncate text-12 text-ink-3">{line.description}</span>
          )}
          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {line.process && (
              <ProcessChip processId={line.process.id} processName={line.process.name} />
            )}
            {line.sublineCount > 0 && (
              <Badge variant="outline">
                {line.sublineCount} {line.sublineCount === 1 ? 'subline' : 'sublines'}
              </Badge>
            )}
            <Badge variant="outline">
              {line.equipmentCount} {line.equipmentCount === 1 ? 'piece' : 'pieces'} of equipment
            </Badge>
            <SessionRollupPill count={rollup} modified={touchedRollup} deleted={deletedRollup} />
          </span>
        </span>
      </PlantHref>
    </div>
  )
}

function ProcessChip({
  processId,
  processName
}: {
  processId: string
  processName: string
}): React.ReactElement {
  return (
    <PlantHref
      id={processId}
      label="Process"
      onClick={(event) => event.stopPropagation()}
      className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand-soft px-2 py-0.5 text-11 font-medium text-brand no-underline hover:bg-brand-soft/70"
    >
      <span aria-hidden>↳</span>
      {processName}
    </PlantHref>
  )
}

function Stat({
  label,
  value,
  added = 0,
  modified = 0,
  deleted = 0
}: {
  label: string
  value: number
  added?: number
  modified?: number
  deleted?: number
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
      <div className="flex items-baseline gap-1.5">
        <div className="text-2xl font-semibold tabular-nums text-ink">{value}</div>
        <SessionRollupPill added={added} modified={modified} deleted={deleted} />
      </div>
      <div className="text-11 font-semibold uppercase tracking-[0.04em] text-ink-3">{label}</div>
    </div>
  )
}

function SectionHeader({
  label,
  subtitle
}: {
  label: string
  subtitle?: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-baseline gap-3">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">{label}</h3>
      {subtitle && <span className="text-xs text-ink-3">{subtitle}</span>}
    </div>
  )
}

function DetailFallback({
  title,
  hint,
  muted
}: {
  title: string
  hint: string
  muted?: boolean
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      {muted ? (
        <div className="text-sm text-ink-3">{title}</div>
      ) : (
        <Empty title={title} hint={hint} icon={WarningCircleIcon} />
      )}
    </div>
  )
}

export default SiteDetailPage
